customElements.get("video-reels") ||
  customElements.define(
    "video-reels",
    class extends HTMLElement {
      constructor() {
        super();

        this.activeClass = "active";
        this.swiper = this.querySelector(".wt-slider__container");

        this.sectionIsVisible = false;
        this.checkInterval = null;
        this.intersectionObserver = null;
        this.syncTimer = null;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        if (this.checkInterval) {
          clearInterval(this.checkInterval);
        }

        if (this.syncTimer) {
          clearTimeout(this.syncTimer);
        }

        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }

        this.pauseAllVideos();
      }

      findActiveSlide(swiper) {
        if (!swiper?.el) return null;

        return swiper.el.querySelector(".swiper-slide-active");
      }

      getActiveVideo(swiper) {
        return this.findActiveSlide(swiper)?.querySelector("video") || null;
      }

      pauseAllVideos() {
        this.querySelectorAll("video").forEach((video) => {
          video.pause();
        });
      }

      toggleActiveClass(swiper) {
        const activeSlide = this.findActiveSlide(swiper);

        if (!activeSlide) return;

        swiper.slides.forEach((slide) => {
          slide.classList.toggle(
            this.activeClass,
            slide === activeSlide
          );
        });
      }

      prepareVideo(video) {
        if (!video) return;

        video.playsInline = true;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");

        video.preload = "auto";

        if (video.readyState === 0) {
          try {
            video.load();
          } catch (error) {}
        }
      }

      preloadNearbyVideos(swiper) {
        if (!swiper?.slides?.length) return;

        const slides = Array.from(swiper.slides);
        const activeSlide = this.findActiveSlide(swiper);

        if (!activeSlide) return;

        const activeIndex = slides.indexOf(activeSlide);

        if (activeIndex === -1) return;

        const indexes = [
          activeIndex,
          activeIndex + 1,
          activeIndex - 1,
        ];

        indexes.forEach((index) => {
          const slide = slides[index];

          if (!slide) return;

          const video = slide.querySelector("video");

          if (video) {
            this.prepareVideo(video);
          }
        });
      }

      updateAllVideosSound(swiper) {
        const soundOn = swiper.el.dataset.sound === "on";

        this.querySelectorAll("video").forEach((video) => {
          video.muted = !soundOn;

          if (!soundOn) {
            video.defaultMuted = true;
          }
        });
      }

      async forcePlayActiveVideo(swiper) {
        if (!swiper || !this.sectionIsVisible) return;

        const activeSlide = this.findActiveSlide(swiper);

        if (!activeSlide) return;

        const video = activeSlide.querySelector("video");

        if (!video) {
          this.pauseAllVideos();
          return;
        }

        this.prepareVideo(video);

        const isStillActive = () => {
          return (
            this.sectionIsVisible &&
            this.findActiveSlide(swiper) === activeSlide
          );
        };

        const tryPlay = async () => {
          if (!isStillActive()) return false;

          try {
            await video.play();

            return !video.paused;
          } catch (error) {
            return false;
          }
        };

        const soundOn = swiper.el.dataset.sound === "on";

        video.muted = !soundOn;

        if (!soundOn) {
          video.defaultMuted = true;
        }

        if (await tryPlay()) {
          return;
        }

        if (!isStillActive()) return;

        /*
         * iOS can reject autoplay when sound is enabled.
         * Always fall back to muted playback.
         */
        video.muted = true;
        video.defaultMuted = true;

        if (await tryPlay()) {
          if (soundOn) {
            swiper.el.dataset.sound = "off";
            this.updateAllVideosSound(swiper);
          }

          return;
        }

        /*
         * Give WebKit two rendered frames to settle after Swiper.
         */
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
          });
        });

        if (await tryPlay()) {
          return;
        }

        /*
         * If the file is already ready, retry shortly.
         */
        if (video.readyState >= 2) {
          await new Promise((resolve) => {
            setTimeout(resolve, 150);
          });

          if (await tryPlay()) {
            return;
          }
        }

        /*
         * If iOS is still loading the media, start it as soon
         * as the video becomes playable.
         */
        const retryWhenReady = async () => {
          if (!isStillActive()) return;

          video.muted = true;
          video.defaultMuted = true;

          await tryPlay();
        };

        video.addEventListener(
          "loadeddata",
          retryWhenReady,
          { once: true }
        );

        video.addEventListener(
          "canplay",
          retryWhenReady,
          { once: true }
        );
      }

      syncActiveSlide(swiper) {
        if (!swiper) return;

        /*
         * Cancel any previously scheduled sync so loopFix,
         * touchEnd and transitionEnd cannot compete.
         */
        if (this.syncTimer) {
          clearTimeout(this.syncTimer);
        }

        this.syncTimer = setTimeout(() => {
          this.syncTimer = null;

          if (typeof swiper.updateSlidesClasses === "function") {
            swiper.updateSlidesClasses();
          }

          /*
           * Always stop everything before deciding what
           * should play now.
           */
          this.pauseAllVideos();

          const activeSlide = this.findActiveSlide(swiper);

          if (!activeSlide) return;

          this.toggleActiveClass(swiper);
          this.preloadNearbyVideos(swiper);

          /*
           * Image blocks intentionally have no video.
           */
          const video = activeSlide.querySelector("video");

          if (!video) return;

          if (!this.sectionIsVisible) return;

          this.forcePlayActiveVideo(swiper);
        }, 100);
      }

      handleSoundToggle(swiper) {
        swiper.slides.forEach((slide) => {
          const button = slide.querySelector(
            ".wt-video__sound-toggle"
          );

          const video = slide.querySelector("video");

          if (!button || !video) return;

          if (
            button.dataset.soundListenerAttached === "true"
          ) {
            return;
          }

          button.dataset.soundListenerAttached = "true";

          button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            const soundOn =
              swiper.el.dataset.sound === "on";

            swiper.el.dataset.sound =
              soundOn ? "off" : "on";

            this.updateAllVideosSound(swiper);

            const activeVideo =
              this.getActiveVideo(swiper);

            if (
              activeVideo &&
              activeVideo.paused &&
              this.sectionIsVisible
            ) {
              this.forcePlayActiveVideo(swiper);
            }
          });
        });
      }

      observeSection(swiper) {
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }

        this.intersectionObserver =
          new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.sectionIsVisible =
                  entry.isIntersecting;

                if (entry.isIntersecting) {
                  this.preloadNearbyVideos(swiper);
                  this.syncActiveSlide(swiper);
                } else {
                  this.pauseAllVideos();
                }
              });
            },
            {
              root: null,
              rootMargin: "200px 0px 200px 0px",
              threshold: 0.01,
            }
          );

        this.intersectionObserver.observe(this);
      }

      addVideoEventHandlers(swiper) {
        if (!swiper) return;

        /*
         * Stop the currently playing video as soon as the user
         * actually starts moving the slider.
         */
        swiper.on("sliderFirstMove", () => {
          this.pauseAllVideos();
        });

        swiper.on("slideChangeTransitionStart", () => {
          this.pauseAllVideos();
        });

        swiper.on("slideChange", () => {
          this.pauseAllVideos();
        });

        /*
         * General transitionEnd is important with loop:true.
         */
        swiper.on("transitionEnd", (swp) => {
          this.syncActiveSlide(swp);
        });

        swiper.on("slideChangeTransitionEnd", (swp) => {
          this.syncActiveSlide(swp);
        });

        swiper.on("touchEnd", (swp) => {
          this.syncActiveSlide(swp);
        });

        swiper.on("loopFix", (swp) => {
          this.syncActiveSlide(swp);
        });

        swiper.on("slideResetTransitionEnd", (swp) => {
          this.syncActiveSlide(swp);
        });
      }

      checkSwiperInitialization() {
        const swiperContainer = this.swiper;

        if (!swiperContainer) return;

        const swiper = swiperContainer.swiper;

        if (
          !swiperContainer.classList.contains(
            "swiper-initialized"
          ) ||
          !swiper
        ) {
          return;
        }

        this.addVideoEventHandlers(swiper);
        this.handleSoundToggle(swiper);

        this.toggleActiveClass(swiper);
        this.preloadNearbyVideos(swiper);

        this.observeSection(swiper);

        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }

      init() {
        this.checkInterval = setInterval(
          this.checkSwiperInitialization.bind(this),
          250
        );
      }
    }
  );